import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Badge, Button, ButtonGroup, Input } from "reactstrap";
import {
  applyAnchorToRow,
  inferResolutionFromMultiPolygon,
  multiPolygonToRows,
  polygonRowError,
  rowsToMultiPolygon,
} from "../../../../utils/polygonUtils";

/**
 * Multipolygon editor for a view's `view_area`.
 *
 * A view area is the API's multipolygon shape: `[[[x, y], …], …]` or `null`.
 * Each polygon is edited on its own row — a `x,y;x,y;…` text field plus four
 * anchor buttons (Top / Bottom / Left / Right) that pin the polygon's endpoints
 * to a frame edge. The pinned endpoints are shown as a compact badge under the
 * field so the anchor is visible at a glance.
 *
 * Frame size: anchors (especially Bottom/Right) need the camera frame
 * dimensions. We take them from the view's own `resolution` (passed in) and
 * fall back to a manual 1920×1080 override when it is unknown — anchor
 * *detection* still works without it (see `detectAnchor`), so existing anchored
 * polygons display correctly regardless.
 *
 * Only a frame the operator typed is reported (`onResolutionChange`), and only
 * once both sides are valid. An inferred or default frame is a display guess:
 * persisting it would store a size nobody checked as the view's resolution.
 *
 * State design: the row list is LOCAL state seeded from `value`. Empty/invalid
 * rows stay visible for editing but are filtered out of the emitted value, so a
 * freshly added row would never survive a naive "derive rows from value" round
 * trip. To keep such rows alive while still reacting to genuine external changes
 * (load / revert / save), we re-seed from `value` only when it differs from what
 * we last emitted (`lastEmittedRef`) — ignoring the echo of our own `onChange`.
 */

const ANCHORS = [
  { key: "top", label: "Top" },
  { key: "bot", label: "Bottom" },
  { key: "left", label: "Left" },
  { key: "right", label: "Right" },
];

const ANCHOR_LABEL = Object.fromEntries(ANCHORS.map(({ key, label }) => [key, label]));

const DEFAULT_RESOLUTION = { width: 1920, height: 1080 };

// Bounds manager-api validates on write; one out-of-range side fails the whole changeset.
const MIN_FRAME_SIDE = 1;
const MAX_FRAME_SIDE = 32768;

const isValidFrameSide = (n) => Number.isInteger(n) && n >= MIN_FRAME_SIDE && n <= MAX_FRAME_SIDE;

const sameValue = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

function PolygonRow({ row, index, anchorsDisabled, onChangeText, onPickAnchor, onRemove }) {
  const error = polygonRowError(row);
  const middleCount = row.middleText.trim() === "" ? 0 : row.middleText.split(";").filter(Boolean).length;
  const totalCount = middleCount + (row.anchor ? 2 : 0);
  const isAnchored = !!row.anchor;

  return (
    <div className="mb-2 p-2" style={{ border: "1px solid #3a3c52", borderRadius: 6 }}>
      <div className="d-flex align-items-center mb-1">
        <span style={{ fontSize: "0.78rem", color: "#aaa" }}>Polygon {index + 1}</span>
        <span style={{ flex: 1 }} />
        <Button
          outline
          color="danger"
          size="sm"
          onClick={onRemove}
          title="Remove this polygon"
          style={{ padding: "0 6px" }}
        >
          <i className="fas fa-trash-alt" />
        </Button>
      </div>

      <Input
        type="textarea"
        bsSize="sm"
        rows={2}
        value={row.middleText}
        // Editing a polygon clears its anchor, mirroring the legacy behaviour.
        onChange={(e) => onChangeText(e.target.value)}
        placeholder="x1,y1;x2,y2;x3,y3 …  (leave empty to drop this polygon)"
        style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
        invalid={!!error}
      />

      {isAnchored && row.firstPoint && row.lastPoint && (
        <div style={{ fontSize: "0.72rem", color: "#7fbfff", marginTop: 4 }}>
          <i className="fas fa-thumbtack mr-1" />
          {ANCHOR_LABEL[row.anchor]} anchor — pinned <code>{row.firstPoint}</code> → <code>{row.lastPoint}</code>
        </div>
      )}

      <div className="d-flex align-items-center mt-2" style={{ gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: "0.75rem", color: "#aaa" }}>Anchor:</span>
        <ButtonGroup size="sm">
          {ANCHORS.map((a) => (
            <Button
              key={a.key}
              color={row.anchor === a.key ? "primary" : "secondary"}
              outline={row.anchor !== a.key}
              disabled={anchorsDisabled}
              title={anchorsDisabled ? "Fix the frame size first" : undefined}
              onClick={() => onPickAnchor(a.key)}
            >
              {a.label}
            </Button>
          ))}
          <Button
            outline
            color="secondary"
            disabled={!isAnchored}
            onClick={() => onPickAnchor("")}
            title="Clear anchor"
          >
            <i className="fas fa-times" />
          </Button>
        </ButtonGroup>
        <span style={{ flex: 1 }} />
        {error ? (
          <span className="text-danger" style={{ fontSize: "0.75rem" }}>
            ⚠️ {error}
          </span>
        ) : (
          <Badge color="secondary">
            {totalCount} vertex{totalCount === 1 ? "" : "es"}
            {isAnchored && ` (${middleCount} + 2 anchor)`}
          </Badge>
        )}
      </div>
    </div>
  );
}

PolygonRow.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.number.isRequired,
    middleText: PropTypes.string.isRequired,
    anchor: PropTypes.string.isRequired,
    firstPoint: PropTypes.string,
    lastPoint: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  // An invalid frame must not pin anything: the view would not store that frame.
  anchorsDisabled: PropTypes.bool.isRequired,
  onChangeText: PropTypes.func.isRequired,
  onPickAnchor: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

// Best frame size we can offer: the view's real resolution, else inferred from
// the polygons' own extent, else the 1920×1080 default. Inferring keeps "add
// anchor" sensible on small DVRs even when the real resolution isn't available.
const effectiveResolution = (resolutionProp, value) =>
  resolutionProp || inferResolutionFromMultiPolygon(value) || DEFAULT_RESOLUTION;

export default function PolygonEditor({ value, resolution: resolutionProp, onChange, onResolutionChange }) {
  const [resolution, setResolution] = useState(() => effectiveResolution(resolutionProp, value));
  const idRef = useRef(0);
  const lastEmittedRef = useRef(value ?? null);

  const makeRows = (val, res) =>
    multiPolygonToRows(val, res).map((row) => {
      idRef.current += 1;
      return { id: idRef.current, ...row };
    });

  const [rows, setRows] = useState(() => makeRows(value, effectiveResolution(resolutionProp, value)));

  // Follow the view's resolution when it arrives or changes, including back to
  // null on Revert: a stale typed frame would pin the next anchor to a size the
  // view no longer carries.
  useEffect(() => {
    setResolution(effectiveResolution(resolutionProp, value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(resolutionProp ?? null)]);

  // Re-seed from `value` only on a genuine external change (load / revert /
  // save) — never on the echo of our own emit, which would wipe in-progress rows.
  useEffect(() => {
    if (sameValue(value, lastEmittedRef.current)) return;
    lastEmittedRef.current = value ?? null;
    const res = effectiveResolution(resolutionProp, value);
    setResolution(res);
    setRows(makeRows(value, res));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (nextRows) => {
    const next = rowsToMultiPolygon(nextRows);
    lastEmittedRef.current = next;
    onChange(next);
  };

  const commit = (nextRows) => {
    setRows(nextRows);
    emit(nextRows);
  };

  const patchRow = (id, patch) => commit(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  // Editing the text invalidates any anchor (its pinned endpoints no longer apply).
  const changeText = (id, middleText) => patchRow(id, { middleText, anchor: "", firstPoint: "", lastPoint: "" });

  const pickAnchor = (id, anchor) =>
    commit(rows.map((row) => (row.id === id ? applyAnchorToRow(row, anchor, resolution) : row)));

  const frameValid = isValidFrameSide(resolution.width) && isValidFrameSide(resolution.height);

  const removeRow = (id) => commit(rows.filter((row) => row.id !== id));

  const addRow = () => {
    idRef.current += 1;
    commit([...rows, { id: idRef.current, middleText: "", anchor: "", firstPoint: "", lastPoint: "" }]);
  };

  // Changing the shared frame re-pins every anchored row to the new edges. An
  // invalid side is only shown: re-pinning to it would save polygons pinned to
  // a frame the view never stores.
  const changeFrameSide = (field, raw) => {
    // Whole digits only: parseInt would read a number input's "1e4" as 1 and "12.9" as 12.
    const n = /^\d+$/.test(raw) ? Number(raw) : NaN;
    const nextRes = { ...resolution, [field]: Number.isNaN(n) ? "" : n };
    setResolution(nextRes);
    if (!isValidFrameSide(nextRes.width) || !isValidFrameSide(nextRes.height)) return;
    commit(rows.map((row) => (row.anchor ? applyAnchorToRow(row, row.anchor, nextRes) : row)));
    onResolutionChange({ width: nextRes.width, height: nextRes.height });
  };

  return (
    <div>
      {rows.length === 0 && (
        <div style={{ fontSize: "0.8rem", color: "#888", marginBottom: 6 }}>No polygons defined for this view.</div>
      )}

      {rows.map((row, index) => (
        <PolygonRow
          key={row.id}
          row={row}
          index={index}
          anchorsDisabled={!frameValid}
          onChangeText={(text) => changeText(row.id, text)}
          onPickAnchor={(anchor) => pickAnchor(row.id, anchor)}
          onRemove={() => removeRow(row.id)}
        />
      ))}

      <div className="d-flex align-items-center mt-2" style={{ gap: 8, flexWrap: "wrap" }}>
        <Button outline color="primary" size="sm" onClick={addRow}>
          <i className="fas fa-plus mr-1" /> Add polygon
        </Button>
        <span style={{ flex: 1 }} />
        <span
          style={{ fontSize: "0.72rem", color: "#888" }}
          title="Camera frame size — used to pin Bottom/Right anchors"
        >
          Frame:
        </span>
        <Input
          bsSize="sm"
          type="number"
          value={resolution.width}
          onChange={(e) => changeFrameSide("width", e.target.value)}
          invalid={!isValidFrameSide(resolution.width)}
          min={MIN_FRAME_SIDE}
          max={MAX_FRAME_SIDE}
          style={{ width: 80 }}
          title="Frame width (for Right anchor)"
        />
        <span style={{ color: "#888" }}>×</span>
        <Input
          bsSize="sm"
          type="number"
          value={resolution.height}
          onChange={(e) => changeFrameSide("height", e.target.value)}
          invalid={!isValidFrameSide(resolution.height)}
          min={MIN_FRAME_SIDE}
          max={MAX_FRAME_SIDE}
          style={{ width: 80 }}
          title="Frame height (for Bottom anchor)"
        />
      </div>

      <div style={{ fontSize: "0.72rem", color: "#888", marginTop: 4 }}>
        Each polygon is <code>x,y</code> pairs separated by <code>;</code>. Pick an anchor to pin the polygon endpoints
        to a frame edge. Empty or invalid polygons are dropped on save.
      </div>
    </div>
  );
}

PolygonEditor.propTypes = {
  // Multipolygon: array of polygons, each polygon an array of [x, y] pairs.
  value: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))),
  // Real camera frame size for this view, when known (drives anchor pinning).
  resolution: PropTypes.shape({ width: PropTypes.number, height: PropTypes.number }),
  onChange: PropTypes.func.isRequired,
  // Separate from onChange, which fires on every polygon edit: only a typed frame may be persisted.
  onResolutionChange: PropTypes.func,
};

PolygonEditor.defaultProps = { value: null, resolution: null, onResolutionChange: () => {} };
